import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Alert, Box, Button, Divider, useTheme } from '@mui/material';
import React from 'react';
import { useRestoreSanitizedCaseMutation } from '../../../../graphql/generated';
import { MetadataRow } from '../../atoms';
import { CaseStatus, DeletedSanitizedCase } from '../../utils/types';

interface SanitizedCaseExpandedProps {
  items: DeletedSanitizedCase[];
  onRestore: () => void;
}

export const SanitizedCaseExpanded: React.FC<SanitizedCaseExpandedProps> = ({
  items,
  onRestore,
}) => {
  const { tokens } = useTheme<LensTheme>();
  const restoreMutation = useRestoreSanitizedCaseMutation();

  const caseItem = items[0];
  if (!caseItem) return null;
  const { metadata, status } = caseItem;

  const handleRestore = async () => {
    await restoreMutation.mutateAsync({ caseId: caseItem.id });
    onRestore();
  };

  return (
    <Box sx={{ width: '100%', pt: tokens.core.spacing[1].value }}>
      <Divider />

      {status === CaseStatus.DELETED && (
        <Alert
          severity="warning"
          sx={{
            mt: tokens.core.spacing[2].value,
            mb: tokens.core.spacing[1].value,
          }}
        >
          This case was fully deleted. No personal data or case content remains.
          Restoring will reinstate the case shell and metadata only.
        </Alert>
      )}

      <MetadataRow label="Original case ID" value={metadata.originalCaseId} />
      <MetadataRow label="Applied policy" value={metadata.appliedPolicy} />
      <MetadataRow label="Case status" value={metadata.caseStatus} />
      {metadata.caseClosed && (
        <MetadataRow label="Case closed" value={metadata.caseClosed} />
      )}
      {status === CaseStatus.SANITIZED && metadata.anonymizedOn && (
        <MetadataRow label="Anonymized on" value={metadata.anonymizedOn} />
      )}
      {status === CaseStatus.DELETED && metadata.deletedOn && (
        <MetadataRow label="Deleted on" value={metadata.deletedOn} />
      )}
      <MetadataRow label="Substantiation" value={metadata.substantiation} />
      <MetadataRow label="Retention period" value={metadata.retentionPeriod} />
      <MetadataRow label="Country" value={metadata.country} />

      {status === CaseStatus.SANITIZED && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: tokens.core.spacing[2].value,
          }}
        >
          <Button
            variant="contained"
            onClick={handleRestore}
            disabled={restoreMutation.isLoading}
          >
            {restoreMutation.isLoading ? 'Restoring...' : 'Restore case'}
          </Button>
        </Box>
      )}
    </Box>
  );
};
